<template>
  <dx-modal
    title="Emitir Receita"
    size="md"
    :value="show"
    :closable="!sending"
    @input="close"
    id="modal-print-recipe"
  >
    <div class="print-section-header">
      <div class="print-section-title">Receita digital</div>
      <div class="print-section-subtitle">
        Assine com validade jurídica e envie direto para o celular do paciente.
      </div>
    </div>
    <div class="print-feature" :class="{ 'is-active': form.signature.show }">
      <div class="print-feature-icon">
        <fa-icon :icon="['fal', 'signature']" />
      </div>
      <div class="print-feature-content">
        <div class="print-feature-title">
          <span>Assinar digitalmente</span>
          <span class="print-feature-badge">Novidade</span>
        </div>
        <div class="print-feature-description">
          Use suas credenciais <strong>Bird ID</strong> para assinar a receita
          com certificado ICP-Brasil.
        </div>
        <div class="print-hint" v-if="blockedReason">
          <fa-icon :icon="['fal', 'info-circle']" /> {{ blockedReason }}
        </div>
        <template v-else-if="form.signature.show">
          <div class="columns print-feature-fields">
            <div class="column col-auto">
              <label class="form-label text-small">Mostrar data</label>
              <select class="form-select" v-model="form.signature.showDate">
                <option :value="true">Sim</option>
                <option :value="false">Não</option>
              </select>
            </div>
            <div class="column col-auto">
              <label class="form-label text-small">Formato</label>
              <select class="form-select" v-model="form.signature.pageSize">
                <option v-for="size in pageSizes" :value="size" :key="size">{{ size }}</option>
              </select>
            </div>
          </div>
          <div class="print-signature-note">
            A receita é emitida em uma via, com validade jurídica ICP-Brasil.
            Depois de assinada, não pode mais ser alterada.
          </div>
        </template>
      </div>
      <div class="print-feature-switch">
        <label class="form-switch">
          <input type="checkbox"
                 :disabled="!!blockedReason"
                 v-model="form.signature.show">
          <i class="form-icon"></i>
        </label>
      </div>
    </div>
    <div class="print-feature" :class="{ 'is-active': form.sendWhatsApp.show }">
      <div class="print-feature-icon">
        <fa-icon :icon="['fab', 'whatsapp']" />
      </div>
      <div class="print-feature-content">
        <div class="print-feature-title">
          <span>Enviar para o paciente pelo WhatsApp</span>
          <span class="print-feature-badge">Novidade</span>
        </div>
        <div class="print-feature-description" v-if="form.signature.show">
          Assim que a assinatura for concluída, o paciente recebe o link da receita,
          válido por 30 dias, para apresentar na farmácia.
        </div>
        <div class="print-feature-description" v-else>
          O paciente recebe agora o link da receita no WhatsApp, válido por 30 dias,
          gerado com as opções de receituário marcadas abaixo.
        </div>
        <div class="print-hint" v-if="whatsAppUnavailableReason">
          <fa-icon :icon="['fal', 'info-circle']" /> {{ whatsAppUnavailableReason }}
        </div>
        <div class="columns print-feature-fields" v-else-if="form.sendWhatsApp.show">
          <div
            class="column col-auto form-group print-feature-phone"
            :class="{ 'has-error': $v.form.sendWhatsApp.cellphone.$error }"
          >
            <label class="form-label text-small" for="print-recipe-cellphone">
              Celular do paciente
            </label>
            <input type="text" class="form-input"
                   id="print-recipe-cellphone"
                   v-model="form.sendWhatsApp.cellphone"
                   @blur="$v.form.sendWhatsApp.cellphone.$touch()"
                   :disabled="sending"
                   placeholder="(00) 0 0000-0000"
                   v-mask-phone.br>
            <template v-if="$v.form.sendWhatsApp.cellphone.$error">
              <div class="form-input-hint"
                   v-if="!$v.form.sendWhatsApp.cellphone.required">Campo obrigatório</div>
              <div class="form-input-hint"
                   v-else-if="!$v.form.sendWhatsApp.cellphone.phone">Telefone inválido</div>
            </template>
          </div>
          <div class="column col-auto form-group" v-if="!form.signature.show">
            <label class="form-label text-small" for="print-recipe-whatsapp-size">Formato</label>
            <select class="form-select"
                    id="print-recipe-whatsapp-size"
                    v-model="form.sendWhatsApp.pageSize"
                    :disabled="sending">
              <option v-for="size in pageSizes" :value="size" :key="size">{{ size }}</option>
            </select>
          </div>
        </div>
      </div>
      <div class="print-feature-switch">
        <label class="form-switch">
          <input type="checkbox"
                 :disabled="!canSendWhatsApp || sending"
                 v-model="form.sendWhatsApp.show">
          <i class="form-icon"></i>
        </label>
      </div>
    </div>
    <div class="print-group" v-if="!form.signature.show">
      <div class="print-section-header">
        <div class="print-section-title">Receituário(s)</div>
        <div class="print-section-subtitle">Receita sem assinatura digital.</div>
      </div>
      <div class="print-body mb-1">
        <div class="print-image">
          <fa-icon :icon="['fal', 'file']" />
        </div>
        <div>
          <div class="text-bold">
            Receituário simples
          </div>
          <div class="columns">
            <div class="column col-auto">
              <label class="form-label text-small">Mostrar data</label>
              <select class="form-select"
                      v-model="form.simpleRecipe.showDate"
                      :disabled="!form.simpleRecipe.show">
                <option value="true">Sim</option>
                <option value="false">Não</option>
              </select>
            </div>
            <div class="column col-auto">
              <label class="form-label text-small">Vias</label>
              <select class="form-select"
                      v-model="form.simpleRecipe.quantity"
                      :disabled="!form.simpleRecipe.show">
                <option value="1">1 via</option>
                <option value="2">2 vias</option>
                <option value="3">3 vias</option>
                <option value="4">4 vias</option>
              </select>
            </div>
          </div>
        </div>
        <div>
          <div class="float-right">
            <label class="form-switch">
              <input type="checkbox"
                     v-model="form.simpleRecipe.show">
              <i class="form-icon"></i>
            </label>
          </div>
        </div>
      </div>
      <div class="print-divider" />
      <div class="print-body">
        <div class="print-image">
          <fa-icon :icon="['fal', 'file-invoice']" />
        </div>
        <div>
          <div class="text-bold">Receituário de controle especial</div>
          <div class="columns">
            <div class="column col-auto">
              <label class="form-label text-small">Mostrar data</label>
              <select class="form-select"
                      v-model="form.specialRecipe.showDate"
                      :disabled="!form.specialRecipe.show">
                <option value="true">Sim</option>
                <option value="false">Não</option>
              </select>
            </div>
            <div class="column col-auto">
              <label class="form-label text-small">Vias</label>
              <select class="form-select"
                      v-model="form.specialRecipe.quantity"
                      :disabled="!form.specialRecipe.show">
                <option value="1">1 via</option>
                <option value="2">2 vias</option>
                <option value="3">3 vias</option>
                <option value="4">4 vias</option>
              </select>
            </div>
          </div>
          <div v-for="(medicine, m) in specialControlMedicines" :key="`${medicine.id}${m}`">
            <small>{{ medicine.name }}</small>
          </div>
        </div>
        <div>
          <div class="float-right">
            <label class="form-switch">
              <input type="checkbox"
                     v-model="form.specialRecipe.show"
                     :disabled="!hasSpecialControl">
              <i class="form-icon"></i>
            </label>
          </div>
        </div>
      </div>
    </div>
    <template slot="footer">
      <div class="d-flex float-right">
        <button
          v-if="form.signature.show"
          type="button"
          class="btn btn-primary btn-icon btn-icon-left"
          @click="sign"
        ><fa-icon :icon="['fal', 'signature']" />
          {{ form.sendWhatsApp.show ? 'Assinar e enviar' : 'Assinar receita' }}</button>
        <template v-else-if="form.sendWhatsApp.show">
          <st-button-report
            type="pdf"
            button-class="btn btn-gray-outline"
            :params="printParams"
            :route="printRoute"
            :disabled="!hasPrintOptions || sending"
          ></st-button-report>
          <button
            type="button"
            class="btn btn-primary btn-icon btn-icon-left ml-1"
            :class="{ loading: sending }"
            :disabled="!hasPrintOptions || sending"
            @click="sendWhatsApp"
          ><fa-icon :icon="['fab', 'whatsapp']" />Enviar pelo WhatsApp</button>
        </template>
        <st-button-report
          v-else
          type="pdf"
          button-class="btn btn-primary"
          :params="printParams"
          :route="printRoute"
          :disabled="!hasPrintOptions"
        ></st-button-report>
        <button class="btn ml-1" :disabled="sending" @click="close">Sair</button>
      </div>
    </template>
  </dx-modal>
</template>

<script>
import { requiredIf } from 'vuelidate/lib/validators';
import { getRecipeBlockedReason, pageSizes } from '@/data/digital-signature';
import { phone } from '@/data/validators';
import { isWhatsappSendEnabled, sendRecipeWhatsapp, whatsappText } from '@/helpers/digital-prescription';

export default {
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    medicalRecordId: {
      type: String,
      required: true,
    },
    recipe: {
      type: Object,
      required: true,
    },
    patient: {
      type: Object,
      default: null,
    },
    professionalName: {
      type: String,
      default: '',
    },
    printDate: {
      type: Boolean,
      default: true,
    },
    canSign: {
      type: Boolean,
      default: true,
    },
    startSigning: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      pageSizes,
      sending: false,
      form: this.blankForm(),
    };
  },
  created() {
    this.form.signature.show = this.startSigning && !this.blockedReason;
  },
  validations() {
    return {
      form: {
        sendWhatsApp: {
          cellphone: {
            required: requiredIf(() => this.form.sendWhatsApp.show),
            phone,
          },
        },
      },
    };
  },
  watch: {
    canSendWhatsApp(value) {
      if (!value) {
        this.form.sendWhatsApp.show = false;
        this.$v.form.sendWhatsApp.$reset();
      }
    },
  },
  computed: {
    drugs() {
      return this.recipe.drugs || [];
    },
    specialControlMedicines() {
      return this.drugs.filter(({ specialControl }) => specialControl);
    },
    hasSimpleControl() {
      return this.drugs.some(({ specialControl }) => !specialControl);
    },
    hasSpecialControl() {
      return this.specialControlMedicines.length > 0;
    },
    isDigitalSignatureEnabled() {
      const settings = this.$store.getters.branchSettings;
      return !!(settings && settings.digitalSignature && settings.digitalSignature.enabled);
    },
    blockedReason() {
      return getRecipeBlockedReason(this.recipe, {
        enabled: this.isDigitalSignatureEnabled,
        canSign: this.canSign,
      });
    },
    isWhatsAppEnabled() {
      return isWhatsappSendEnabled();
    },
    whatsAppUnavailableReason() {
      return this.isWhatsAppEnabled ? '' : 'Envio pelo WhatsApp não habilitado';
    },
    canSendWhatsApp() {
      return this.isWhatsAppEnabled;
    },
    hasPrintOptions() {
      return this.form.specialRecipe.show || this.form.simpleRecipe.show;
    },
    printRoute() {
      return `/medical-records/${this.medicalRecordId}/recipes/${this.recipe.id}/print`;
    },
    printParams() {
      const params = {};
      if (this.form.simpleRecipe.show) {
        params.simpleRecipe = true;
        params.simpleDate = this.form.simpleRecipe.showDate;
        params.simpleQuantity = this.form.simpleRecipe.quantity;
      }
      if (this.form.specialRecipe.show) {
        params.specialRecipe = true;
        params.specialDate = this.form.specialRecipe.showDate;
        params.specialQuantity = this.form.specialRecipe.quantity;
      }
      return params;
    },
  },
  methods: {
    sign() {
      if (this.blockedReason) {
        return;
      }
      const sendWhatsApp = this.canSendWhatsApp && this.form.sendWhatsApp.show;
      if (sendWhatsApp) {
        this.$v.form.sendWhatsApp.$touch();
        if (this.$v.form.sendWhatsApp.$error) {
          return;
        }
      }
      this.$emit('sign', {
        showDate: this.form.signature.showDate,
        pageSize: this.form.signature.pageSize,
        whatsappCellphone: sendWhatsApp ? this.form.sendWhatsApp.cellphone : '',
      });
    },
    async sendWhatsApp() {
      if (this.sending || !this.canSendWhatsApp || !this.hasPrintOptions) {
        return;
      }

      this.$v.form.sendWhatsApp.$touch();
      if (this.$v.form.sendWhatsApp.$error) {
        return;
      }

      const route = `/medical-records/${this.medicalRecordId}/recipes/${this.recipe.id}`;
      const params = {
        ...this.printParams,
        reportType: `pdf:${this.form.sendWhatsApp.pageSize.toLowerCase()}`,
      };

      this.sending = true;
      try {
        const result = await sendRecipeWhatsapp(this, {
          cellphone: this.form.sendWhatsApp.cellphone,
          messageRoute: `${route}/message`,
          shareRoute: `${route}/share`,
          params,
          buildText: documentUrl => whatsappText({
            patientName: this.patient ? this.patient.name : '',
            professionalName: this.professionalName,
            documentUrl,
          }),
        });
        this.sending = false;
        if (result) {
          this.close();
        }
      } finally {
        this.sending = false;
      }
    },
    close() {
      if (this.sending) {
        return;
      }
      this.$emit('close');
    },
    blankForm() {
      const drugs = this.recipe.drugs || [];
      return {
        signature: {
          show: false,
          showDate: this.printDate,
          pageSize: pageSizes[0],
        },
        sendWhatsApp: {
          show: false,
          cellphone: this.patient && this.patient.cellphone ? this.patient.cellphone : '',
          pageSize: pageSizes[0],
        },
        simpleRecipe: {
          show: !!this.recipe.manipulated || drugs.some(({ specialControl }) => !specialControl),
          showDate: this.printDate,
          quantity: 1,
        },
        specialRecipe: {
          show: drugs.some(({ specialControl }) => specialControl),
          showDate: this.printDate,
          quantity: 2,
        },
      };
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/_variables.scss";

#modal-print-recipe {
  .print-section-header {
    margin-bottom: .6rem;
  }
  .print-section-title {
    color: $gray-color-900;
    font-size: $font-size-lg;
    font-weight: 700;
    line-height: 1.25;
  }
  .print-section-subtitle {
    color: $gray-color-dark;
    font-size: $font-size-sm;
    margin-top: .1rem;
  }
  .print-hint {
    color: $gray-color-dark;
    font-size: $font-size-sm;
    margin-top: $layout-spacing;
    svg {
      color: $primary-color;
    }
  }
  .print-feature {
    align-items: flex-start;
    background-image: linear-gradient(
      135deg,
      lighten($primary-color, 37%),
      lighten($primary-color, 32%)
    );
    border: $border-width solid lighten($primary-color, 27%);
    border-radius: $border-radius;
    display: flex;
    gap: .7rem;
    margin-bottom: .6rem;
    padding: .8rem 1rem;
    transition: border-color .15s, box-shadow .15s;
    &.is-active {
      border-color: lighten($primary-color, 15%);
      box-shadow: 0 .1rem .4rem rgba($primary-color, .12);
    }
  }
  .print-feature-icon {
    align-items: center;
    background-color: $light-color;
    border-radius: 50%;
    box-shadow: 0 .1rem .3rem rgba($primary-color, .2);
    color: $primary-color;
    display: flex;
    flex: 0 0 2rem;
    font-size: $font-size-lg;
    height: 2rem;
    justify-content: center;
  }
  .print-feature-content {
    flex: 1;
    min-width: 0;
  }
  .print-feature-title {
    align-items: center;
    color: $gray-color-900;
    display: flex;
    flex-wrap: wrap;
    font-weight: 700;
    gap: .2rem .5rem;
    line-height: 1.4;
  }
  .print-feature-badge {
    background-color: $light-color;
    border: $border-width solid $primary-color;
    border-radius: 1rem;
    color: $primary-color;
    font-size: $font-size-xs;
    font-weight: 600;
    line-height: 1;
    padding: .15rem .45rem;
  }
  .print-feature-description {
    color: $gray-color-dark;
    font-size: $font-size-sm;
    line-height: 1.45;
    margin-top: .15rem;
  }
  .print-feature-fields {
    margin-top: .5rem;
  }
  .print-feature-phone {
    max-width: 11rem;
  }
  .print-feature-switch {
    flex-shrink: 0;
    .form-switch {
      margin: 0;
      padding-right: 0;
    }
  }
  .print-signature-note {
    background-color: rgba($light-color, .7);
    border: $border-width solid lighten($primary-color, 27%);
    border-radius: $border-radius;
    color: $gray-color-dark;
    font-size: $font-size-sm;
    margin-top: .6rem;
    padding: $layout-spacing-sm $layout-spacing;
  }
  .print-group {
    margin: $layout-spacing-lg 0;
    .print-divider {
      border-bottom: $border-width dashed $border-color;
    }
    .print-body {
      display: grid;
      grid-template-columns: 1fr 4fr 2fr;
      padding: $layout-spacing;
      .print-image {
        align-items: center;
        justify-content: center;
        display: flex;
        background: $gray-color-ultra-light;
        border-radius: 50px;
        height: 2.5rem;
        width: 2.5rem;
        svg {
          color: $gray-color-dark;
          font-size: $font-size-lg;
        }
      }
    }
  }
  @media (max-width: $size-sm) {
    .print-feature {
      flex-wrap: wrap;
    }
    .print-feature-content {
      flex-basis: calc(100% - 5rem);
    }
  }
}
</style>
